// Public Account & Data Deletion page.
// PUBLIC: no auth. Required for App Store account deletion guidance.
// URL: https://roava.wgpapp.com/account-deletion

import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const SUPPORT_EMAIL = "support@watsongrowthpartners.com";
const LAST_UPDATED = "June 2026";

export const Route = createFileRoute("/account-deletion")({
  head: () =>
    pageMeta({
      title: "Account & Data Deletion — Roava",
      description:
        "How to request deletion of your Roava account, trip data, uploaded media, and related information.",
      path: "/account-deletion",
    }),
  component: AccountDeletionPage,
});

function AccountDeletionPage() {
  return (
    <MarketingShell>
      <article className="mx-auto max-w-2xl pt-2">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Your account</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
            Account & Data Deletion
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </header>

        <div className="space-y-6 text-sm sm:text-[15px] leading-relaxed text-foreground/90">
          <section className="rounded-2xl border border-primary/40 bg-primary/5 p-5 sm:p-6">
            <h2 className="text-lg font-semibold">Fastest way: delete in the app</h2>
            <p className="mt-2">
              Open Roava and go to <strong>Profile → Danger zone → Delete account</strong>.
              You'll be asked to type <strong>DELETE</strong> to confirm, then your account
              and data are removed immediately — no email needed.
            </p>
          </section>

          <section className="rounded-2xl border border-border bg-card/60 p-5 sm:p-6">
            <h2 className="text-lg font-semibold">Can't access the app?</h2>
            <p className="mt-2">
              If you've lost access to your device, email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              from the address tied to your Roava account with the subject line
              "Account deletion request".
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              We process these requests within <strong>7–10 business days</strong>.
            </p>
          </section>


          <Section title="What deletion removes">
            <p>When we process your request, we delete:</p>
            <ul className="mt-2 list-disc pl-5 space-y-1">
              <li>Your account information (email, display name, profile details).</li>
              <li>Trip and itinerary data tied to your account.</li>
              <li>Uploaded photos and videos you own in trip libraries.</li>
              <li>Recorded route points and queued offline GPS data.</li>
              <li>Family Follow share links and their access settings.</li>
              <li>Notification device tokens registered to your account.</li>
              <li>
                Roava-stored Health/activity data you exported into a trip, where applicable.
              </li>
            </ul>
          </Section>

          <Section title="What deletion does not remove">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Original photos and videos in your device's Photos library. Those remain on
                your device unless you delete them yourself.
              </li>
              <li>
                Apple Health data inside the Apple Health app. Roava does not delete data
                from Apple Health; you manage Health data through the Health app and iOS
                Settings.
              </li>
              <li>
                Trip content other travelers have already saved or re-shared inside their
                own trip records.
              </li>
            </ul>
          </Section>

          <Section title="Records we may retain">
            <p>
              Some information may be retained where required or permitted by law, or to
              maintain the integrity of the service — for example, security logs, backup
              snapshots that rotate out over time, fraud-prevention records, and records
              related to optional in-app purchases or disputes.
            </p>
          </Section>

          <Section title="Need help?">
            <p>
              See{" "}
              <Link to="/support" className="text-primary hover:underline">
                Support
              </Link>{" "}
              for common questions, or review our{" "}
              <Link to="/privacy" className="text-primary hover:underline">
                Privacy Policy
              </Link>
              .
            </p>
          </Section>
        </div>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-semibold tracking-tight mt-8 mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
